import { useIntl } from 'react-intl';
import { KeyRound, Asterisk, LockKeyhole } from 'lucide-react';
import { foundation } from '../../../ui';
import { useResponsiveLayout } from '../../../hooks/ui/useResponsiveLayout';
import { authenticatorStoreLinks } from '../../../utils/auth/authenticatorStoreLinks';

const f = foundation;
const fc = f.color;

// Per-provider tiles — Lucide glyphs on each vendor's own brand colour (no
// copied logos). These three hexes are the vendors' colours, not CrewVex
// tokens, which is why they live here and nowhere else.
const PROVIDER_TILE = {
  apple: 'linear-gradient(180deg, #3a9bff 0%, #1f6bff 100%)',
  google: '#1a73e8',
  microsoft: '#0078d4',
};

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: f.spacePx[3],
  minHeight: 52,
  padding: `${f.spacePx[2]} 0`,
  borderTop: `1px solid ${fc.neutral.border}`,
  boxSizing: 'border-box',
};

const tileStyle = (background) => ({
  flexShrink: 0,
  width: 32,
  height: 32,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background,
  color: '#fff',
});

const textWrap = { flex: 1, minWidth: 0 };
const nameStyle = { fontSize: f.fontSize.sm, fontWeight: f.fontWeight.semibold, color: fc.neutral.text, fontFamily: f.fontFamily.sans, lineHeight: 1.3 };
const subStyle = { fontSize: f.fontSize.xs, color: fc.neutral.textMuted, fontFamily: f.fontFamily.sans, marginTop: 2 };
const linkStyle = (compact) => ({
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: compact ? 44 : 32,
  minWidth: compact ? 44 : undefined,
  border: 'none',
  background: 'transparent',
  color: fc.brand.text,
  fontWeight: f.fontWeight.bold,
  fontSize: f.fontSize.sm,
  fontFamily: f.fontFamily.sans,
  cursor: 'pointer',
  textDecoration: 'none',
  padding: `0 ${f.spacePx[2]}`,
  boxSizing: 'border-box',
});

const GLYPH = { size: 16, strokeWidth: 2.2 };

/**
 * RD-SEC-015 — "¿No tienes app?" disclosure list (flow A intro step).
 * Apple Passwords is informational only — it has no documented URL scheme,
 * so it carries no action button (the step-1 `otpauth://` link is what opens
 * Passwords). Google / Microsoft link out to the OS-appropriate store.
 */
export default function MfaAuthenticatorAppList() {
  const intl = useIntl();
  const { isMobile, isTablet } = useResponsiveLayout();
  const compact = isMobile || isTablet;
  const stores = authenticatorStoreLinks();
  const getLabel = intl.formatMessage({ id: 'mfaStepper.get', defaultMessage: 'Get' });
  const freeLabel = intl.formatMessage({ id: 'mfaStepper.free', defaultMessage: 'Free' });

  return (
    <div>
      <div style={{ ...rowStyle, borderTop: 'none' }}>
        <span style={tileStyle(PROVIDER_TILE.apple)} aria-hidden data-testid="mfa-app-tile-apple">
          <KeyRound {...GLYPH} />
        </span>
        <div style={textWrap}>
          <div style={nameStyle}>{intl.formatMessage({ id: 'mfaStepper.apple', defaultMessage: 'Apple Passwords' })}</div>
          <div style={subStyle}>{intl.formatMessage({ id: 'mfaStepper.appleSub', defaultMessage: 'Built into your iPhone' })}</div>
        </div>
      </div>
      <div style={rowStyle}>
        <span style={tileStyle(PROVIDER_TILE.google)} aria-hidden data-testid="mfa-app-tile-google">
          <Asterisk {...GLYPH} />
        </span>
        <div style={textWrap}>
          <div style={nameStyle}>{intl.formatMessage({ id: 'mfaStepper.google', defaultMessage: 'Google Authenticator' })}</div>
          <div style={subStyle}>{freeLabel}</div>
        </div>
        <a href={stores.google} target="_blank" rel="noreferrer" style={linkStyle(compact)}>{getLabel}</a>
      </div>
      <div style={rowStyle}>
        <span style={tileStyle(PROVIDER_TILE.microsoft)} aria-hidden data-testid="mfa-app-tile-microsoft">
          <LockKeyhole {...GLYPH} />
        </span>
        <div style={textWrap}>
          <div style={nameStyle}>{intl.formatMessage({ id: 'mfaStepper.microsoft', defaultMessage: 'Microsoft Authenticator' })}</div>
          <div style={subStyle}>{freeLabel}</div>
        </div>
        <a href={stores.microsoft} target="_blank" rel="noreferrer" style={linkStyle(compact)}>{getLabel}</a>
      </div>
    </div>
  );
}
